// Pure rules behind the Command Center moderation override dialog: which
// overrides a post's moderation read model allows, which checks an outage
// release covers by default, the request payloads, and how the server's
// answers read. Nothing here fetches or renders.
import { checkFailureWords, holdReasonLabel, isOpenReview } from './moderationReviewModel'
import { checkFailureClass, checksWords, notEnforcedSentence, providerFaultFailure } from './moderationWords'

const WHITESPACE_RUN_PATTERN = /\s+/g

export { checkWords, checksWords } from './moderationWords'

// The provider checks an outage release can cover, in the order the server
// sorts them.
export const OUTAGE_CHECKS = [
  { value: 'provider_text', label: 'Text', description: 'The provider check of the post\'s text.' },
  { value: 'provider_image', label: 'Images', description: 'The provider check of each image the post shows, video posters included.' },
  { value: 'provider_video', label: 'Video', description: 'The provider check of the video\'s frames.' },
  { value: 'provider_audio', label: 'Soundtrack', description: 'The provider check of the video\'s speech.' },
]

const EXEMPTION_TYPE_LABELS = {
  provider_unavailable: 'Provider outage release',
  disabled_release: 'Released while moderation was off',
  disabled: 'Published while moderation was off',
  grandfathered: 'Grandfathered: existed before moderation',
}

const EXEMPTION_SUBJECT_LABELS = {
  post: 'This post',
  asset: 'Media used by this post',
}

// exemptionTypeLabel names an exemption type; an unknown one is spelled
// out from its code.
export function exemptionTypeLabel(type) {
  return EXEMPTION_TYPE_LABELS[type] || String(type || 'Exemption').replaceAll('_', ' ')
}

function formatCreatedAt(createdAt) {
  if (!createdAt) return ''
  const date = new Date(createdAt)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString()
}

const NOT_ENFORCED_LABELS = {
  platform_off: 'Off for every brand (platform switch)',
  platform_shadow: 'Observe-only: checks are recorded, nothing is held',
  org_off: 'Off for this brand',
}

// moderationStateLabel is the dialog's one-line answer to "does moderation
// enforce this post, and if not, which switch is off".
export function moderationStateLabel(moderation) {
  if (moderation?.enforced) return 'Enforced'
  return NOT_ENFORCED_LABELS[moderation?.not_enforced_reason] || 'Not enforced'
}

// notCheckedLabel says, for a post moderation enforces but never checked,
// whether it went live while moderation was off; '' otherwise.
export function notCheckedLabel(moderation) {
  if (!moderation?.not_checked) return ''
  return moderation.exemption?.type === 'disabled'
    ? 'Published while moderation was off; never checked'
    : 'Never checked under enforcement'
}

// releasedByLabel names the exemption that lets an exempt post through: its
// type, who granted it (Wager when the viewer may not see who) and when,
// and whether it still counts; '' unless the post is exempt.
export function releasedByLabel(moderation) {
  const exemption = moderation?.exemption
  if (moderation?.status !== 'exempt' || !exemption) return ''
  const parts = [exemptionTypeLabel(exemption.type), exemption.granted_by_email || 'Wager']
  const granted = formatCreatedAt(exemption.granted_at)
  if (granted) parts.push(granted)
  if (exemption.counts === false) parts.push('no longer counts')
  return parts.join(' · ')
}

const TITLE_LIMIT = 80

// overrideDialogDescription names the post and its organization under the
// dialog title, the post cut to one readable line.
export function overrideDialogDescription(postTitle, organizationName) {
  const title = String(postTitle || '').replace(WHITESPACE_RUN_PATTERN, ' ').trim()
  const short = Array.from(title).length > TITLE_LIMIT ? `${Array.from(title).slice(0, TITLE_LIMIT - 1).join('').trimEnd()}…` : title
  return [short, organizationName].filter(Boolean).join(' · ')
}

// exemptionView puts one active exemption of the read model into words.
export function exemptionView(exemption) {
  return {
    id: exemption?.id,
    type: exemptionTypeLabel(exemption?.type),
    subject: EXEMPTION_SUBJECT_LABELS[exemption?.subject_type] || String(exemption?.subject_type || ''),
    checks: checksWords(exemption?.checks_covered) || 'No checks named',
    reason: exemption?.reason || '',
    created: formatCreatedAt(exemption?.created_at),
    grantedBy: exemption?.granted_by_email || '',
    stale: exemption?.counts === false,
  }
}

// A release while moderation is off lifts a hold on a check that has not
// run.
const OFF_RELEASABLE_HOLDS = new Set(['provider_pending', 'provider_unavailable', 'asset_pending'])

// A review hold or a rejection is a brand decision while its review is
// open. A release while moderation is off lifts one only when the hold was
// carried onto a revision edited while the switch was off: that edit
// superseded the review and none was opened, so the queue has nothing to
// decide. A rejection a brand admin decided in review stays.
const DECISION_HOLDS = new Set(['human_review', 'rejected'])

function carriedDecisionHold(moderation) {
  return DECISION_HOLDS.has(moderation?.hold_reason)
    && !isOpenReview(moderation?.review)
    && moderation?.review?.status !== 'rejected'
}

// An outage release applies to a check that failed, a provider that is
// unavailable, or media still waiting for a check: a soundtrack longer than
// the transcription limit completes with incomplete coverage and waits for
// good, and the server accepts that incomplete result as evidence.
const OUTAGE_HOLDS = new Set(['provider_unavailable', 'asset_pending'])

// canReleaseForOutage: a provider outage release applies while moderation
// enforces the post and a check failed for good on a provider fault (class
// outage or refused: the server's outage evidence takes every recorded code
// that is not a content error), the provider is unavailable or media waits
// for a check. A failure the same input would repeat (the media, the
// content, the regulations) is no outage.
export function canReleaseForOutage(moderation) {
  if (!moderation || moderation.enforced !== true) return false
  if (moderation.check_failed === true) return providerFaultFailure(moderation.failure_code)
  return OUTAGE_HOLDS.has(moderation.hold_reason)
}

// canReleaseWhileOff: a release while moderation is off applies to a post
// held waiting for a check its organization's switch keeps from running, or
// to a review hold or rejection carried onto a revision no review is open
// for.
export function canReleaseWhileOff(moderation) {
  if (!moderation || moderation.enforced !== false) return false
  return OFF_RELEASABLE_HOLDS.has(moderation.hold_reason) || carriedDecisionHold(moderation)
}

// offReleaseDescription says what a release while moderation is off does
// for this post.
export function offReleaseDescription(moderation) {
  const releases = 'Releasing records an exemption for the missing checks and publishes this revision if nothing else holds it.'
  if (carriedDecisionHold(moderation)) {
    return `No review is open for this revision: its hold was carried onto an edit made while moderation was off, so the moderation queue has nothing to decide. ${releases}`
  }
  return `${notEnforcedSentence(moderation?.not_enforced_reason)}, so the missing checks will not run. ${releases}`
}

// outageReleaseNotice warns that only part of a long soundtrack was checked,
// or, for media still waiting for a check, that the release is refused
// unless a check has an outage or an incomplete result on record. It is ''
// for any other hold.
export function outageReleaseNotice(moderation) {
  if (moderation?.check_incomplete && (moderation.incomplete_checks || []).includes('provider_audio')) {
    return 'Only the first 30 minutes of the soundtrack were checked: it is longer than the transcription limit. Release it only after someone has listened to the rest.'
  }
  if (moderation?.hold_reason !== 'asset_pending') return ''
  return 'The media checks are still waiting. Wager refuses this release unless a check has a provider outage or an incomplete result on record, such as a soundtrack longer than the 30-minute transcription limit.'
}

// overrideOptions says which release the dialog offers. The two never apply
// together: one needs moderation on, the other off.
export function overrideOptions(moderation) {
  return {
    outage: canReleaseForOutage(moderation),
    offRelease: canReleaseWhileOff(moderation),
  }
}

// noOverrideExplanation says why neither release applies, or '' when one
// does.
export function noOverrideExplanation(moderation) {
  if (!moderation) return ''
  const { outage, offRelease } = overrideOptions(moderation)
  if (outage || offRelease) return ''
  const failure = nonOutageFailureNotice(moderation)
  if (failure) return failure
  const { status, hold_reason: hold, review } = moderation
  if (status === 'rejected' || hold === 'rejected') {
    if (review?.status === 'auto_rejected') {
      return 'Moderation rejected this revision automatically. A brand admin decides it in the moderation queue, where the rejection can be overturned; Wager does not release it.'
    }
    if (review?.status === 'rejected') {
      return "A brand admin rejected this revision in review. Wager does not release it. The author can edit the post, or a brand admin can overturn the rejection from the review's History."
    }
    return 'Moderation rejected this revision. Wager does not release rejections: the author edits the post to submit a new revision.'
  }
  if (status === 'flagged' || hold === 'human_review') {
    return moderation.enforced
      ? 'This post is held for brand review. A brand admin decides it in the moderation queue; Wager does not release review holds.'
      : 'This post is held for brand review. A brand admin decides it in the moderation queue, which keeps working while moderation is not enforced.'
  }
  if (status === 'passed' || status === 'exempt' || !hold) {
    return 'Nothing holds this post, so there is nothing to release.'
  }
  return 'Its checks are still running. A provider outage release becomes available if a check fails or the provider is unavailable.'
}

// renderedMedia reports which kinds of media the post shows: an image media
// object, or a video (whose recorded poster is an image too).
export function renderedMedia(post) {
  let image = false
  let video = false
  for (const media of post?.media_objects || []) {
    const type = String(media?.type || '').toLowerCase()
    if (type === 'image') image = true
    if (type === 'video') {
      video = true
      if (media?.metadata?.poster_asset_id) image = true
    }
  }
  return { image, video }
}

// defaultOutageChecks preselects the checks that look missing: the image
// check when the post shows an image and the video and soundtrack checks
// when it shows a video, plus the text check unless only media is waiting
// (an asset_pending hold).
export function defaultOutageChecks(post, moderation) {
  const { image, video } = renderedMedia(post)
  return [
    ...(moderation?.hold_reason === 'asset_pending' ? [] : ['provider_text']),
    ...(image ? ['provider_image'] : []),
    ...(video ? ['provider_video', 'provider_audio'] : []),
  ]
}

// outageChecksHelp says where the preselected checks came from.
export function outageChecksHelp(moderation) {
  return moderation?.hold_reason === 'asset_pending'
    ? 'Preselected from the media the post shows: its images, video and soundtrack.'
    : 'Preselected from what the post shows: text, plus its images, video and soundtrack.'
}

export function toggleCheck(checks, check, selected) {
  const next = new Set(checks || [])
  if (selected) next.add(check)
  else next.delete(check)
  return OUTAGE_CHECKS.map((entry) => entry.value).filter((value) => next.has(value))
}

// versionsKnown reports whether the read model carries every version an
// override echoes. The policy fingerprint is empty for a post whose targets
// do not resolve to a moderation policy; no override applies to it.
export function versionsKnown(moderation) {
  return Number.isInteger(moderation?.revision) && moderation.revision > 0
    && Boolean(moderation.content_hash) && Boolean(moderation.policy_fingerprint)
}

export const UNRESOLVED_POLICY_MESSAGE = 'This post\'s regulations do not resolve to a moderation policy, so no release applies until its targets are fixed.'

// The versions the operator was shown; the server refuses an override
// against anything newer.
function expectedVersions(moderation) {
  return {
    expected_revision: moderation?.revision,
    expected_hash: moderation?.content_hash,
    expected_policy: moderation?.policy_fingerprint,
  }
}

export function outageReleasePayload(moderation, checks, reason) {
  const chosen = new Set(checks || [])
  return {
    action: 'provider_unavailable',
    checks: OUTAGE_CHECKS.map((entry) => entry.value).filter((value) => chosen.has(value)),
    ...expectedVersions(moderation),
    reason: String(reason || '').trim(),
  }
}

// A release while moderation is off names no checks: the server covers
// every missing one, and refuses a named set that differs from them.
export function offReleasePayload(moderation, reason) {
  return {
    action: 'disabled_release',
    ...expectedVersions(moderation),
    reason: String(reason || '').trim(),
  }
}

export function canSubmitOutageRelease(moderation, checks, reason) {
  return canReleaseForOutage(moderation) && versionsKnown(moderation)
    && (checks || []).length > 0 && String(reason || '').trim() !== ''
}

export function canSubmitOffRelease(moderation, reason) {
  return canReleaseWhileOff(moderation) && versionsKnown(moderation) && String(reason || '').trim() !== ''
}

// nonOutageFailureNotice says why a failed check is not an outage and what
// gets the post checked; '' for an outage or no failure.
export function nonOutageFailureNotice(moderation) {
  if (moderation?.check_failed !== true) return ''
  switch (checkFailureClass(moderation.failure_code)) {
    case 'media':
      return `This check failed on the media itself (${checkFailureWords(moderation.failure_code)}), not on the provider, so no outage release applies. Replace the media and save the post to check it again.`
    case 'content':
      return 'This check failed because the provider refused the content itself, not because of an outage, so no outage release applies. The author changes the text or media and saves the post to check it again.'
    case 'scope':
      return 'This check failed because one of the post\'s regulations is not set up for moderation, not because of an outage, so no outage release applies. Set up the regulation for this brand or change the post\'s regulations; the post is checked again once they resolve.'
    default:
      return ''
  }
}

// An asset exemption the organization endpoint cannot find may sit on a
// shared library asset no organization owns; the read model cannot tell.
export const ASSET_REVOKE_NOT_FOUND_MESSAGE = 'This exemption could not be revoked from this organization. It may belong to a shared Command Center library asset.'

export const OVERRIDE_STALE_MESSAGE = 'This post changed since you opened it. It has been refreshed: check it again before you act.'

// classifyOverrideError maps a refused override or revocation onto what the
// dialog says. A stale refusal refreshes the post.
export function classifyOverrideError(error, { exemption } = {}) {
  const status = error?.response?.status
  const data = error?.response?.data || {}
  if (status === 409 && data.code === 'moderation_override_stale') {
    return { kind: 'stale', message: OVERRIDE_STALE_MESSAGE }
  }
  let message = data.error || error?.message || 'The action could not be applied.'
  if (status === 404 && exemption?.subject_type === 'asset') {
    message = ASSET_REVOKE_NOT_FOUND_MESSAGE
  }
  return { kind: 'error', message }
}

export function overrideSuccessMessage(result) {
  if (result?.released) {
    return result.action === 'disabled_release'
      ? 'Post released while moderation is off'
      : 'Post released for the provider outage'
  }
  const hold = holdReasonLabel(result?.hold_reason)
  const covered = checksWords(result?.checks)
  return `Exemption recorded${covered ? ` for ${covered}` : ''}. The post is still held${hold ? `: ${hold.toLowerCase()}` : ''}.`
}

export function revokeSuccessMessage(result) {
  const withdrawn = Boolean(result?.withdrawn) || (result?.posts || []).some((post) => post?.withdrawn)
  if (withdrawn) return 'Exemption revoked. The post left the feed until it is checked again.'
  if (result?.reevaluated || (result?.posts || []).length) return 'Exemption revoked. The post was evaluated again without it.'
  return 'Exemption revoked'
}
